import type { ColumnDef } from '@tanstack/react-table';
import { Package, Plus } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { PartTypePicker } from '@/components/ui/part-type-picker';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePartTypes } from '@/features/part-types/queries';
import { useParts } from '@/features/parts/queries';
import { isGlobalPart, type Part } from '@/features/parts/types';
import { ApiError } from '@/lib/api';
import { matchesQuery } from '@/lib/list-search';
import { cn } from '@/lib/utils';

/** Loading skeleton — 5 rows × 7 columns matching the table shape. */
function ListTableSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: 7 }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-20" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: 7 }).map((_, c) => (
              <TableCell key={c}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** "Global ISI" / "Custom" pill, indigo for global to set it apart. */
function ScopeBadge({ row }: { row: Part }) {
  const { t } = useTranslation();
  const global = isGlobalPart(row);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        global
          ? 'bg-indigo-100 text-indigo-700'
          : 'bg-amber-100 text-amber-800',
      )}
    >
      {t(`part.scope.${global ? 'global' : 'company'}`)}
    </span>
  );
}

export function PartsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const partTypeId = searchParams.get('partTypeId') ?? undefined;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useParts({ partTypeId });
  const { data: partTypes } = usePartTypes();

  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'' | 'global' | 'company'>('');
  const deferredSearch = useDeferredValue(search);

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list
      .filter((p) =>
        scopeFilter === ''
          ? true
          : scopeFilter === 'global'
            ? isGlobalPart(p)
            : !isGlobalPart(p),
      )
      .filter((p) =>
        matchesQuery(p, deferredSearch, [
          (x) => x.code,
          (x) => x.name,
          (x) => x.manufacturer,
          (x) => x.manufacturerPartNumber,
          (x) => x.notes,
        ]),
      );
  }, [data, deferredSearch, scopeFilter]);

  // Resolve partTypeId → name for the column. Memoised Map for O(1)
  // lookup so rows render in O(n).
  const partTypesMap = useMemo(() => {
    const m = new Map<string, string>();
    (partTypes ?? []).forEach((pt) => m.set(pt.id, pt.name));
    return m;
  }, [partTypes]);

  // Resolve the part's effective unit: own override or parent type's
  // default. Same Map lookup approach.
  const partTypeUnitsMap = useMemo(() => {
    const m = new Map<string, string>();
    (partTypes ?? []).forEach((pt) => m.set(pt.id, pt.defaultUnit));
    return m;
  }, [partTypes]);

  const columns = useMemo<ColumnDef<Part>[]>(
    () => [
      {
        accessorKey: 'code',
        header: () => t('part.columns.code'),
        cell: ({ row }) => (
          <Link
            to={`/parts/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-sm text-primary hover:underline"
          >
            {row.original.code}
          </Link>
        ),
      },
      {
        accessorKey: 'name',
        header: () => t('part.columns.name'),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: 'partType',
        header: () => t('part.columns.partType'),
        enableSorting: false,
        cell: ({ row }) =>
          partTypesMap.get(row.original.partTypeId) ?? '—',
      },
      {
        accessorKey: 'manufacturer',
        header: () => t('part.columns.manufacturer'),
        cell: ({ getValue }) => getValue<string | null>() ?? '—',
      },
      {
        id: 'unit',
        header: () => t('part.columns.unit'),
        enableSorting: false,
        cell: ({ row }) => {
          const own = row.original.unit;
          const inherited = partTypeUnitsMap.get(row.original.partTypeId);
          const effective = own ?? inherited ?? '—';
          return <span className="font-mono text-xs">{effective}</span>;
        },
      },
      {
        id: 'scope',
        header: () => t('part.columns.scope'),
        enableSorting: false,
        cell: ({ row }) => <ScopeBadge row={row.original} />,
      },
      {
        accessorKey: 'isActive',
        header: () => t('part.columns.status'),
        enableSorting: false,
        cell: ({ getValue }) => (
          <span
            className={cn(
              'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
              getValue<boolean>()
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {t(`part.status.${getValue<boolean>() ? 'active' : 'inactive'}`)}
          </span>
        ),
      },
    ],
    [t, partTypesMap, partTypeUnitsMap],
  );

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('part.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('part.list.loadError');
  })();

  const setPartTypeFilter = (next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next === null) {
      params.delete('partTypeId');
    } else {
      params.set('partTypeId', next);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('part.list.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('part.list.searchPlaceholder')}
            aria-label={t('common.search')}
            className="w-64"
            data-testid="parts-search"
          />
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('part.list.filterByType')}
            </span>
            <PartTypePicker
              value={partTypeId ?? null}
              onChange={setPartTypeFilter}
              allowEmpty
              emptyLabel={t('part.list.allTypes')}
              className="h-9 w-64"
            />
          </label>
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value as typeof scopeFilter)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            aria-label={t('part.list.filterByScope')}
            data-testid="parts-scope-filter"
          >
            <option value="">{t('part.list.allScopes')}</option>
            <option value="global">{t('part.list.scope.global')}</option>
            <option value="company">{t('part.list.scope.company')}</option>
          </select>
          <Button asChild>
            <Link to="/parts/new">
              <Plus aria-hidden />
              {t('part.list.newButton')}
            </Link>
          </Button>
        </div>
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('part.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isError && (
        <DataTable
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          loadingState={<ListTableSkeleton />}
          emptyState={
            <EmptyState
              icon={Package}
              title={t('part.list.empty')}
              description={t('part.list.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link to="/parts/new">
                    <Plus aria-hidden />
                    {t('part.list.newButton')}
                  </Link>
                </Button>
              }
            />
          }
          onRowClick={(p) => navigate(`/parts/${p.id}`)}
        />
      )}
    </div>
  );
}
